/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import Component from '@glimmer/component';
import { limitedOperators as operators, findOperator } from 'cio/utils/conditions-operators';
import CampaignModel, { FormTriggeredCampaignFormTriggerFilter } from 'cio/models/campaign';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';

interface EditorArgs {
  campaign: CampaignModel;
  condition: EventAttributeCondition;
  disabled: boolean;
  size: string;
  variation: string;
  subLayout?: ConditionEditorSubLayout;
  marginClass: string;
  dropdownClass: string;
  allowComparisonOperator: boolean;
  showEventNameInEditor?: boolean;
  event?: string;
  allowJsonOperators?: boolean;
  isJsonChild?: boolean;
}

export default class Editor extends Component<EditorArgs> {
  @service facebookLeadAds!: FacebookLeadAdsService;

  _operators = operators;

  get allowJsonOperators() {
    return this.args.allowJsonOperators ?? false;
  }

  get showEventNameInEditor() {
    return this.args.showEventNameInEditor ?? true;
  }

  get namePlaceholder() {
    if (this.isJsonChild) {
      return 'value';
    }

    if (this.args.campaign?.isWebhookTriggered) {
      return 'trigger attribute';
    }

    return 'event_attribute_name';
  }

  @computed('args.campaign.{isFormTriggered,attributeFilters}', 'facebookLeadAds.forms.[]')
  get eventFormsNames() {
    if (!this.args.campaign.isFormTriggered) {
      return null;
    }

    let filters = deserializeAndNormalizeFormAttributeFilters(this.args.campaign.attributeFilters);
    let formIDs = filters.children.map((f: FormTriggeredCampaignFormTriggerFilter) => f.children[0].value);

    return formIDs.map(
      (id: string) =>
        // won't find any forms if lead ads integration is disabled: display form ID as fallback
        this.facebookLeadAds.formsByFormID[id]?.formNameForDisplay || `Facebook Form with ID of ${id}`,
    );
  }

  @computed('args.condition.operator')
  get _selectedOperator() {
    return findOperator(this.args.condition.operator);
  }

  @action
  onSelectOperator(operator: { value: string }) {
    this.args.condition.set('operator', operator.value);
  }

  get isJsonChild() {
    return this.args.condition?.isJsonChild ?? false;
  }

  get autoSuggestValues() {
    return !this.isJsonChild;
  }
}
